import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';
import { Account } from '../interfaces/accounting.interface';
import { saveBlob } from '../utils/saveBlob';
import { CompareMode } from '../utils/reportPeriods';
import { parseContentDispositionFilename } from '../utils/contentDisposition';

export type ReportFileFormat = 'pdf' | 'xlsx';

// Server-rendered report download (letterhead, page numbers, Dhaka timestamp): GET as a blob and save.
const downloadReport = async (url: string, params: Record<string, string | boolean | undefined>, fallbackName: string) => {
    let response;
    try {
        response = await axios.get(url, { params, responseType: 'blob' });
    } catch (error: any) {
        // A blob request turns the server's JSON error into a Blob: read its message so the toast is useful.
        const data = error?.response?.data;
        if (data instanceof Blob) {
            try {
                const parsed = JSON.parse(await data.text());
                if (parsed && typeof parsed.message === 'string') error.response.data = parsed;
            } catch {
                // not JSON: keep the original error
            }
        }
        throw error;
    }
    const disposition: string = response.headers?.['content-disposition'] ?? '';
    saveBlob(new Blob([response.data]), parseContentDispositionFilename(disposition, fallbackName));
};

export const AccountService = {
    getAccountsByInstitution: async (page: number = 0, size: number = 10, includeHidden: boolean = false) => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(`${ENDPOINTS.ACCOUNT.LIST_BY_INSTITUTION(institutionId!)}?page=${page}&size=${size}&includeHidden=${includeHidden}`);
        return response.data;
    },
    // Loads the ENTIRE chart of accounts (all pages), not just the first page.
    // Account dropdowns must show every account; a single fixed page size silently
    // dropped leaf accounts once the chart grew past the page limit.
    getAllAccounts: async (includeHidden: boolean = false): Promise<Account[]> => {
        const pageSize = 200; // the server clamps a page to 200 rows
        const first = await AccountService.getAccountsByInstitution(0, pageSize, includeHidden);
        if (Array.isArray(first)) return first;
        const all: Account[] = [...(first.content ?? [])];
        const totalPages: number = first.totalPages ?? 1;
        for (let page = 1; page < totalPages; page++) {
            const next = await AccountService.getAccountsByInstitution(page, pageSize, includeHidden);
            all.push(...(Array.isArray(next) ? next : (next.content ?? [])));
        }
        return all;
    },
    // Names-only chart for the fixed-asset pickers: works for users without ACCOUNT_VIEW (store admin) and
    // returns the same accounts an accountant sees, minus every balance. Use this, not getAllAccounts, on FA screens.
    getLookup: async (includeHidden: boolean = false): Promise<Account[]> => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.ACCOUNT.LOOKUP(institutionId!), { params: { includeHidden } });
        return response.data ?? [];
    },
    getAccountsTree: async () => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.ACCOUNT.TREE_BY_INSTITUTION(institutionId!));
        return response.data;
    },
    getAccountsByType: async (type: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.ACCOUNT.BY_TYPE(institutionId!, type));
        return response.data;
    },
    getAccountById: async (id: string) => {
        const response = await axios.get(ENDPOINTS.ACCOUNT.BY_ID(id));
        return response.data;
    },
    createAccount: async (account: Account) => {
        const response = await axios.post(ENDPOINTS.ACCOUNT.BASE, account);
        return response.data;
    },
    updateAccount: async (id: string, account: Account) => {
        const response = await axios.put(ENDPOINTS.ACCOUNT.UPDATE(id), account);
        return response.data;
    },
    deleteAccount: async (id: string) => {
        await axios.delete(ENDPOINTS.ACCOUNT.DELETE(id));
    },
    hideAccount: async (accountId: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        await axios.post(ENDPOINTS.ACCOUNT.HIDE(institutionId!, accountId));
    },
    unhideAccount: async (accountId: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        await axios.delete(ENDPOINTS.ACCOUNT.UNHIDE(institutionId!, accountId));
    },
    getHiddenAccounts: async (): Promise<string[]> => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.ACCOUNT.HIDDEN(institutionId!));
        return response.data;
    },
    getAccountLedger: async (accountId: string, from: string, to: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.ACCOUNT.LEDGER(accountId), {
            params: { institutionId, from, to },
        });
        return response.data;
    },
    /** includeClosing=false shows the trial balance pre-closing (year-end closing entries left out). */
    getTrialBalance: async (from: string, to: string, includeClosing: boolean = true) => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.ACCOUNT.TRIAL_BALANCE(institutionId!), {
            params: includeClosing ? { from, to } : { from, to, includeClosing: false },
        });
        return response.data;
    },
    getSegmentTrialBalance: async (from: string, to: string, segmentId?: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.ACCOUNT.SEGMENT_TRIAL_BALANCE(institutionId!), { params: { from, to, ...(segmentId ? { segmentId } : {}) } });
        return response.data;
    },
    getIncomeStatement: async (from: string, to: string, segmentId?: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.ACCOUNT.INCOME_STATEMENT(institutionId!), { params: { from, to, ...(segmentId ? { segmentId } : {}) } });
        return response.data;
    },
    getBalanceSheet: async (asOf: string, segmentId?: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.ACCOUNT.BALANCE_SHEET(institutionId!), { params: { asOf, ...(segmentId ? { segmentId } : {}) } });
        return response.data;
    },
    downloadTrialBalance: async (from: string, to: string, format: ReportFileFormat, includeClosing: boolean = true) => {
        const institutionId = AuthService.getSelectedInstitution();
        await downloadReport(ENDPOINTS.ACCOUNT.TRIAL_BALANCE_EXPORT(institutionId!), includeClosing ? { from, to, format } : { from, to, format, includeClosing: false }, `trial-balance-${to}.${format}`);
    },
    downloadIncomeStatement: async (from: string, to: string, segmentId: string | undefined, compare: CompareMode, format: ReportFileFormat) => {
        const institutionId = AuthService.getSelectedInstitution();
        await downloadReport(ENDPOINTS.ACCOUNT.INCOME_STATEMENT_EXPORT(institutionId!),
            { from, to, compare, format, ...(segmentId ? { segmentId } : {}) }, `income-statement-${to}.${format}`);
    },
    downloadBalanceSheet: async (asOf: string, segmentId: string | undefined, comparePreviousYearEnd: boolean, format: ReportFileFormat) => {
        const institutionId = AuthService.getSelectedInstitution();
        await downloadReport(ENDPOINTS.ACCOUNT.BALANCE_SHEET_EXPORT(institutionId!),
            { asOf, comparePreviousYearEnd, format, ...(segmentId ? { segmentId } : {}) }, `balance-sheet-${asOf}.${format}`);
    },
    downloadLedger: async (accountId: string, from: string, to: string, format: ReportFileFormat) => {
        const institutionId = AuthService.getSelectedInstitution();
        await downloadReport(ENDPOINTS.ACCOUNT.LEDGER_EXPORT(accountId), { institutionId: institutionId!, from, to, format }, `account-ledger-${to}.${format}`);
    },
};
